import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authService, tareasService } from '../services/api';
import HtmlContent from '../components/HtmlContent';
import BbcodeContent from '../components/BbcodeContent';
import CodigoEntregaViewer from '../components/CodigoEntregaViewer';
import RichTextEditor from '../components/RichTextEditor';
import { formatEntregaAlumno } from '../utils/formatEntregaAlumno';
import './Tareas.css';

const ROLES_DOCENTE = ['Profesor', 'Tutor', 'Pasante', 'Ayudante', 'Coordinador'];

const getPrismLanguage = (lang) => {
  if (!lang || lang === 'plaintext') return 'plaintext';
  return lang === 'html' ? 'markup' : lang;
};

const formatFechaEntrega = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short', hour12: false });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatFechaCorta = (value) => {
  const iso = toDateInputValue(value);
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const VerCorreccion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entregaId } = useParams();
  const fromCalificaciones = location.state?.from === 'calificaciones';
  const backTo = fromCalificaciones ? '/dashboard/calificaciones' : '/dashboard/tareas';
  const backState = fromCalificaciones
    ? undefined
    : {
        state: {
          tab: 'correccion',
          subTab: location.state?.returnTo?.subTab === 'pendientes' ? 'pendientes' : 'corregidas',
          ...(typeof location.state?.returnTo?.page === 'number' && location.state.returnTo.page >= 1
            ? { page: location.state.returnTo.page }
            : {}),
          ...(location.state?.returnTo?.cursoFiltro !== undefined
            && location.state?.returnTo?.cursoFiltro !== null
            ? { cursoFiltro: location.state.returnTo.cursoFiltro }
            : {}),
        },
      };
  const [entrega, setEntrega] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comentarios, setComentarios] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [errorComentario, setErrorComentario] = useState('');
  const [selectedRole, setSelectedRole] = useState(() => localStorage.getItem('selectedRole') || '');
  const [editando, setEditando] = useState(false);
  const [notaEdit, setNotaEdit] = useState('');
  const [devolucionEdit, setDevolucionEdit] = useState('');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState('');
  const [anularReentrega, setAnularReentrega] = useState(false);
  const [fechaReentregaEdit, setFechaReentregaEdit] = useState('');
  const comentariosEndRef = useRef(null);

  useEffect(() => {
    const syncRole = () => setSelectedRole(localStorage.getItem('selectedRole') || '');
    window.addEventListener('roleChanged', syncRole);
    window.addEventListener('storage', syncRole);
    return () => {
      window.removeEventListener('roleChanged', syncRole);
      window.removeEventListener('storage', syncRole);
    };
  }, []);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await tareasService.getEntregaParaCorregir(entregaId);
        setEntrega(data);
        setNotaEdit(data?.nota != null && data.nota !== '' ? String(data.nota) : '');
        setDevolucionEdit(data?.devolucion || '');
        setFechaReentregaEdit(toDateInputValue(data?.nueva_fecha_cierre));
        setComentarios(Array.isArray(data?.comentarios) ? data.comentarios : []);
      } catch (err) {
        setError(err.response?.data?.error || 'Error al cargar la corrección');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entregaId, navigate]);

  const puedeEditarCorreccion = ROLES_DOCENTE.includes(selectedRole) && Boolean(entrega?.revisado);
  const esReentrega = Boolean(entrega?.pedir_reentrega) || (Array.isArray(entrega?.historial) && entrega.historial.length > 0);

  const guardarEdicion = async (e) => {
    e.preventDefault();
    if (!entrega || guardandoEdicion) return;
    setErrorEdicion('');
    const anular = Boolean(entrega.pedir_reentrega && anularReentrega);
    const fechaReentrega = fechaReentregaEdit.trim();
    const notaObligatoria = Boolean(entrega.se_califica && (!esReentrega || anular));
    let notaPayload = null;
    if (notaObligatoria && notaEdit.trim() === '') {
      setErrorEdicion('La nota es obligatoria.');
      return;
    }
    if (entrega.pedir_reentrega && !anular && !fechaReentrega) {
      setErrorEdicion('Debe indicar la nueva fecha de cierre para la reentrega.');
      return;
    }
    if (entrega.se_califica && notaEdit.trim() !== '') {
      const n = parseFloat(notaEdit);
      if (Number.isNaN(n) || n < 0 || n > 10) {
        setErrorEdicion('La nota debe ser un número entre 0 y 10.');
        return;
      }
      notaPayload = n;
    }
    setGuardandoEdicion(true);
    try {
      const payload = {
        editar_correccion: true,
        devolucion: devolucionEdit || '',
        ...(notaPayload !== null ? { nota: notaPayload } : {}),
        ...(anular
          ? { anular_reentrega: true }
          : (entrega.pedir_reentrega ? { nueva_fecha_cierre: fechaReentrega } : {})),
      };
      const data = await tareasService.corregirEntrega(entrega.id, payload);
      setEntrega(data);
      setNotaEdit(data?.nota != null && data.nota !== '' ? String(data.nota) : '');
      setDevolucionEdit(data?.devolucion || '');
      setFechaReentregaEdit(toDateInputValue(data?.nueva_fecha_cierre));
      setAnularReentrega(false);
      setEditando(false);
    } catch (err) {
      setErrorEdicion(err.response?.data?.error || 'No se pudo guardar la corrección.');
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const descargarAdjuntoEntrega = async (a) => {
    try {
      await tareasService.descargarArchivoEntrega(a.id, a.nombre_original || 'archivo');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo descargar el archivo');
    }
  };

  const enviarComentario = async (e) => {
    e.preventDefault();
    const texto = nuevoComentario.trim();
    if (!texto || !entregaId || enviandoComentario) return;
    setEnviandoComentario(true);
    setErrorComentario('');
    try {
      const creado = await tareasService.crearComentarioEntrega(entregaId, texto);
      setComentarios((prev) => [...prev, creado]);
      setNuevoComentario('');
      setTimeout(() => {
        comentariosEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    } catch (err) {
      setErrorComentario(err.response?.data?.error || 'No se pudo enviar el comentario');
    } finally {
      setEnviandoComentario(false);
    }
  };

  if (loading) {
    return (
      <div className="tareas-loading">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (!entrega) {
    return (
      <div className="tareas-container">
        <div className="tareas-error">{error || 'Corrección no encontrada'}</div>
        <button type="button" className="tareas-btn tareas-btn-secondary" onClick={() => navigate(backTo, backState)}>
          {fromCalificaciones ? 'Volver a Calificaciones' : 'Volver a Tareas'}
        </button>
      </div>
    );
  }

  let contenido = {};
  try {
    contenido = typeof entrega.comentario === 'string' ? JSON.parse(entrega.comentario || '{}') : (entrega.comentario || {});
  } catch (_) {}
  const codigoLenguaje = getPrismLanguage(contenido.codigoLenguaje);
  const archivos = entrega.archivos_list || [];
  const estado = entrega.estado || (entrega.pedir_reentrega
    ? 'Reentrega'
    : entrega.revisado
      ? (entrega.se_califica ? 'Aprobada' : 'Vista')
      : 'Pendiente de corrección');
  const textoLooksLikeHtml = contenido.texto && /<[a-z][\s\S]*>/i.test(String(contenido.texto));

  return (
    <div className="tareas-container">
      <div className="tareas-form-page-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Ver corrección</h1>
        <button
          type="button"
          className="tareas-btn tareas-btn-secondary"
          onClick={() => navigate(backTo, backState)}
        >
          ← Volver
        </button>
      </div>
      <div className="tareas-correccion-info">
        <p><strong>Alumno:</strong> {formatEntregaAlumno(entrega)}</p>
        <p><strong>Curso:</strong> {entrega.curso_nombre || '-'}</p>
        <p><strong>Tarea:</strong> {entrega.tarea_titulo || '-'}</p>
        <p><strong>Fecha de entrega:</strong> {formatFechaEntrega(entrega.fecha_entrega)}</p>
      </div>
      {entrega.tarea_descripcion && (
        <div className="tareas-correccion-descripcion">
          <strong>Descripción de la tarea:</strong>
          <HtmlContent content={entrega.tarea_descripcion} className="tareas-descripcion-text" />
        </div>
      )}
      <div className="tareas-correccion-contenido">
        <strong>Contenido de la entrega</strong>
        {contenido.texto && (
          <div className="tareas-correccion-campo">
            <label>Texto:</label>
            <div className="tareas-correccion-valor tareas-correccion-texto-bbcode">
              {textoLooksLikeHtml ? (
                <HtmlContent content={contenido.texto} tag="div" />
              ) : (
                <BbcodeContent content={contenido.texto} tag="div" />
              )}
            </div>
          </div>
        )}
        {contenido.enlace && (
          <div className="tareas-correccion-campo">
            <label>Enlace:</label>
            <a href={contenido.enlace} target="_blank" rel="noopener noreferrer">{contenido.enlace}</a>
          </div>
        )}
        {contenido.codigo && (
          <div className="tareas-correccion-campo">
            <label>Código:</label>
            <CodigoEntregaViewer codigo={contenido.codigo} language={codigoLenguaje} />
          </div>
        )}
        {archivos.length > 0 && (
          <div className="tareas-correccion-campo tareas-correccion-archivos">
            <label>Archivos adjuntos</label>
            <div className="tareas-correccion-archivos-grid">
              {archivos.map((a) => (
                <div key={a.id} className="tareas-correccion-archivo-card">
                  <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                  <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                    {a.nombre_original || 'Archivo'}
                  </span>
                  <button
                    type="button"
                    className="tareas-correccion-archivo-descargar"
                    onClick={() => descargarAdjuntoEntrega(a)}
                  >
                    Descargar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {!contenido.texto && !contenido.enlace && !contenido.codigo && archivos.length === 0 && (
          <p className="tareas-correccion-sin-contenido">Sin contenido adicional.</p>
        )}
      </div>

      <div className="tareas-correccion-contenido ver-correccion-resultado">
        <strong>Corrección</strong>
        <div className="tareas-correccion-info ver-correccion-meta">
          {entrega.se_califica && entrega.nota != null && (
            <p><strong>Nota:</strong> {String(entrega.nota)}</p>
          )}
          <p><strong>Estado:</strong> {estado}</p>
          {entrega.fecha_revision && (
            <p><strong>Fecha de corrección:</strong> {new Date(entrega.fecha_revision).toLocaleString('es-AR')}</p>
          )}
          {entrega.corregido_por_nombre && (
            <p><strong>Corrigió:</strong> {entrega.corregido_por_nombre}</p>
          )}
          {entrega.pedir_reentrega && entrega.nueva_fecha_cierre && (
            <p><strong>Fecha límite de reentrega:</strong> {formatFechaCorta(entrega.nueva_fecha_cierre)}</p>
          )}
        </div>
        {entrega.devolucion && !editando && (
          <div className="tareas-correccion-campo">
            <label>Devolución al estudiante:</label>
            <div className="ver-correccion-devolucion">
              <HtmlContent content={entrega.devolucion} tag="div" className="html-content" />
            </div>
          </div>
        )}

        {puedeEditarCorreccion && !editando && (
          <div className="ver-correccion-editar-actions">
            <button
              type="button"
              className="tareas-btn tareas-btn-secondary"
              onClick={() => {
                setAnularReentrega(false);
                setErrorEdicion('');
                setFechaReentregaEdit(toDateInputValue(entrega.nueva_fecha_cierre));
                setEditando(true);
              }}
            >
              Modificar nota o devolución
            </button>
          </div>
        )}

        {puedeEditarCorreccion && editando && (
          <form className="ver-correccion-editar-form" onSubmit={guardarEdicion}>
            {entrega.pedir_reentrega && (
              <div className="tareas-form-group ver-correccion-anular">
                <div className="ver-correccion-anular-row">
                  <input
                    id="ver-correccion-anular"
                    type="checkbox"
                    checked={anularReentrega}
                    onChange={(e) => setAnularReentrega(e.target.checked)}
                    disabled={guardandoEdicion}
                  />
                  <span className="ver-correccion-anular-titulo">Anular reentrega</span>
                </div>
                <p className="ver-correccion-anular-ayuda">
                  {entrega.se_califica
                    ? 'La entrega queda corregida con una nota y el estudiante ya no puede reenviar.'
                    : 'La entrega queda revisada y el estudiante ya no puede reenviar.'}
                </p>
              </div>
            )}
            {entrega.pedir_reentrega && !anularReentrega && (
              <div className="tareas-form-group">
                <label htmlFor="ver-correccion-fecha-reentrega">Nueva fecha de cierre para la reentrega *</label>
                <input
                  id="ver-correccion-fecha-reentrega"
                  type="date"
                  value={fechaReentregaEdit}
                  onChange={(e) => setFechaReentregaEdit(e.target.value)}
                  className="tareas-input ver-correccion-fecha-input"
                  required
                  disabled={guardandoEdicion}
                />
              </div>
            )}
            {entrega.se_califica && (!esReentrega || anularReentrega) && (
              <div className="tareas-form-group">
                <label htmlFor="ver-correccion-nota">Nota (0-10) *</label>
                <input
                  id="ver-correccion-nota"
                  type="number"
                  min="0"
                  max="10"
                  step="0.01"
                  value={notaEdit}
                  onChange={(e) => setNotaEdit(e.target.value)}
                  className="tareas-input"
                  required
                  disabled={guardandoEdicion}
                />
              </div>
            )}
            <div className="tareas-form-group">
              <label htmlFor="ver-correccion-devolucion">Devolución al estudiante</label>
              <RichTextEditor
                value={devolucionEdit}
                onChange={setDevolucionEdit}
                placeholder="Escriba la devolución al estudiante..."
                minHeight="140px"
                allowImageUpload
              />
            </div>
            {errorEdicion && (
              <p className="ver-correccion-comentarios-error" role="alert">{errorEdicion}</p>
            )}
            <div className="tareas-form-actions">
              <button
                type="button"
                className="tareas-btn tareas-btn-secondary"
                disabled={guardandoEdicion}
                onClick={() => {
                  setEditando(false);
                  setErrorEdicion('');
                  setAnularReentrega(false);
                  setNotaEdit(entrega.nota != null && entrega.nota !== '' ? String(entrega.nota) : '');
                  setDevolucionEdit(entrega.devolucion || '');
                  setFechaReentregaEdit(toDateInputValue(entrega.nueva_fecha_cierre));
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="tareas-btn tareas-btn-primary" disabled={guardandoEdicion}>
                {guardandoEdicion ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}

        {(entrega.puede_comentar || comentarios.length > 0) && (
          <div className="ver-correccion-comentarios">
            <h3 className="ver-correccion-comentarios-titulo">Comentarios</h3>
            {comentarios.length === 0 ? (
              <p className="ver-correccion-comentarios-vacio">Todavía no hay comentarios en esta devolución.</p>
            ) : (
              <ul className="ver-correccion-comentarios-lista">
                {comentarios.map((c) => (
                  <li
                    key={c.id}
                    className={`ver-correccion-comentario ${c.es_docente ? 'es-docente' : 'es-estudiante'}`}
                  >
                    <div className="ver-correccion-comentario-meta">
                      <span className="ver-correccion-comentario-autor">
                        {c.autor_nombre || 'Usuario'}
                        <span className="ver-correccion-comentario-rol">
                          {c.es_docente ? 'Docente' : 'Estudiante'}
                        </span>
                      </span>
                      <span className="ver-correccion-comentario-fecha">
                        {formatFechaEntrega(c.created_at)}
                      </span>
                    </div>
                    <p className="ver-correccion-comentario-texto">{c.texto}</p>
                  </li>
                ))}
                <li ref={comentariosEndRef} aria-hidden className="ver-correccion-comentarios-anchor" />
              </ul>
            )}
            {entrega.puede_comentar && (
              <form className="ver-correccion-comentarios-form" onSubmit={enviarComentario}>
                <label htmlFor="ver-correccion-nuevo-comentario">Agregar comentario</label>
                <textarea
                  id="ver-correccion-nuevo-comentario"
                  className="ver-correccion-comentarios-textarea"
                  rows={3}
                  maxLength={5000}
                  value={nuevoComentario}
                  onChange={(e) => setNuevoComentario(e.target.value)}
                  placeholder="Escribí un comentario sobre la devolución..."
                  disabled={enviandoComentario}
                />
                {errorComentario && (
                  <p className="ver-correccion-comentarios-error" role="alert">{errorComentario}</p>
                )}
                <div className="ver-correccion-comentarios-actions">
                  <button
                    type="submit"
                    className="tareas-btn tareas-btn-primary"
                    disabled={enviandoComentario || !nuevoComentario.trim()}
                  >
                    {enviandoComentario ? 'Enviando…' : 'Enviar comentario'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {/* Historial de entregas anteriores (reentregas) */}
      {Array.isArray(entrega.historial) && entrega.historial.length > 0 && (
        <div className="ver-correccion-historial">
          <h2 className="ver-correccion-historial-titulo">Historial de entregas</h2>
          {entrega.historial.map((item, index) => {
            let cont = {};
            try {
              cont = typeof item.comentario === 'string' ? JSON.parse(item.comentario || '{}') : (item.comentario || {});
            } catch (_) {}
            const codigoLang = getPrismLanguage(cont.codigoLenguaje);
            const archivosItem = item.archivos_list || [];
            const textoHtml = cont.texto && /<[a-z][\s\S]*>/i.test(String(cont.texto));
            return (
              <div key={item.id} className="ver-correccion-historial-item">
                <h3 className="ver-correccion-historial-item-titulo">
                  Entrega anterior {entrega.historial.length > 1 ? `#${index + 1}` : ''}
                  {item.fecha_entrega && (
                    <span className="ver-correccion-historial-fecha">
                      {' — '}
                      {new Date(item.fecha_entrega).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                </h3>
                <div className="tareas-correccion-contenido ver-correccion-historial-contenido">
                  {cont.texto && (
                    <div className="tareas-correccion-campo">
                      <label>Texto:</label>
                      <div className="tareas-correccion-valor tareas-correccion-texto-bbcode">
                        {textoHtml ? (
                          <HtmlContent content={cont.texto} tag="div" />
                        ) : (
                          <BbcodeContent content={cont.texto} tag="div" />
                        )}
                      </div>
                    </div>
                  )}
                  {cont.enlace && (
                    <div className="tareas-correccion-campo">
                      <label>Enlace:</label>
                      <a href={cont.enlace} target="_blank" rel="noopener noreferrer">{cont.enlace}</a>
                    </div>
                  )}
                  {cont.codigo && (
                    <div className="tareas-correccion-campo">
                      <label>Código:</label>
                      <CodigoEntregaViewer codigo={cont.codigo} language={codigoLang} />
                    </div>
                  )}
                  {archivosItem.length > 0 && (
                    <div className="tareas-correccion-campo tareas-correccion-archivos">
                      <label>Archivos adjuntos</label>
                      <div className="tareas-correccion-archivos-grid">
                        {archivosItem.map((a) => (
                          <div key={a.id} className="tareas-correccion-archivo-card">
                            <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                            <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                              {a.nombre_original || 'Archivo'}
                            </span>
                            <button
                              type="button"
                              className="tareas-correccion-archivo-descargar"
                              onClick={() => descargarAdjuntoEntrega(a)}
                            >
                              Descargar
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="tareas-correccion-contenido ver-correccion-resultado ver-correccion-historial-resultado">
                  <div className="tareas-correccion-info ver-correccion-meta">
                    {item.nota != null && <p><strong>Nota:</strong> {String(item.nota)}</p>}
                    <p><strong>Estado:</strong> {item.estado || '-'}</p>
                    {item.fecha_revision && (
                      <p><strong>Fecha de corrección:</strong> {new Date(item.fecha_revision).toLocaleString('es-AR')}</p>
                    )}
                    {item.corregido_por_nombre && (
                      <p><strong>Corrigió:</strong> {item.corregido_por_nombre}</p>
                    )}
                  </div>
                  {item.devolucion && (
                    <div className="tareas-correccion-campo">
                      <label>Devolución al estudiante:</label>
                      <div className="ver-correccion-devolucion">
                        <HtmlContent content={item.devolucion} tag="div" className="html-content" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VerCorreccion;
